import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  InputAdornment
} from '@mui/material';
import {
  Movie,
  Tv,
  Add,
  Delete,
  Edit,
  Search,
  Upload,
  Analytics,
  FindInPage,
  TrendingUp,
  Schedule,
  Refresh,
  Warning,
  FilterList,
  ViewModule,
  ViewList
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { isAdmin } from '../../utils/adminUtils';
import { mongoAdminApi } from '../../services/mongoAdminApi';
import { s3ImageService } from '../../services/s3ImageService';
import { tmdbService } from '../../services/tmdbService';

const ContentAdminPanel = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ movies: {}, shows: {} });
  const [duplicates, setDuplicates] = useState([]);
  const [trending, setTrending] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [bulkData, setBulkData] = useState('');
  const [movieForm, setMovieForm] = useState({
    name: '',
    genre: '',
    language: '',
    release_date: '',
    rating: '',
    summary: '',
    poster: '',
    hero: '',
    heroine: '',
    director: '',
    music: '',
    screenplay: '',
    editor: '',
    cameraman: '',
    production_house: '',
    ott: '',
    tmdb_id: ''
  });
  const [tmdbResults, setTmdbResults] = useState([]);
  const [tmdbQuery, setTmdbQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [movies, setMovies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [languageFilter, setLanguageFilter] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (isAdmin(user)) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [movieStats, showStats, trendingData] = await Promise.all([
        mongoAdminApi.getMovieStats(),
        mongoAdminApi.getShowStats(),
        mongoAdminApi.getTrendingMovies()
      ]);

      setStats({
        movies: movieStats,
        shows: showStats
      });
      setTrending(trendingData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load dashboard data'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMovies = async (page = 1) => {
    try {
      setLoading(true);
      const limit = 50;
      const offset = (page - 1) * limit;
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sort_by: sortBy,
        sort_order: 'desc',
        content_type: 'movie'
      });
      
      if (languageFilter) params.append('language', languageFilter);
      if (genreFilter) params.append('genre', genreFilter);
      if (searchQuery) params.append('q', searchQuery);
      
      const endpoint = searchQuery ? '/movies/search' : '/movies';
      const response = await fetch(`https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api${endpoint}?${params}`);
      const data = await response.json();
      
      setMovies(data.movies || data.results || []);
      setFilteredMovies(data.movies || data.results || []);
      setTotalPages(Math.ceil((data.total || 0) / limit));
      setCurrentPage(page);
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to load movies'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    // Trigger API search with debounce
    setTimeout(() => loadMovies(1), 300);
  };
  
  const handleFilterChange = () => {
    setCurrentPage(1);
    loadMovies(1);
  };

  const handleEditMovie = (movie) => {
    console.log('Edit movie data:', movie); // Debug log
    const movieId = movie._id || movie.id;
    console.log('Movie ID:', movieId); // Debug log
    
    if (!movieId) {
      addNotification({ type: 'error', message: 'Movie ID is missing from data' });
      return;
    }
    
    setMovieForm({ ...movie, id: movieId }); // Ensure ID is set
    setDialogType('edit');
    setDialogOpen(true);
  };

  const loadDuplicates = async () => {
    try {
      setLoading(true);
      const duplicateData = await mongoAdminApi.findDuplicateMovies();
      setDuplicates(duplicateData);
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to load duplicates'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    try {
      const movies = JSON.parse(bulkData);
      if (!Array.isArray(movies) || movies.length === 0) {
        throw new Error('Invalid data format');
      }
      
      const result = await mongoAdminApi.bulkCreateMovies(movies);
      addNotification({
        type: 'success',
        message: `Successfully uploaded ${result.created || movies.length} movies`
      });
      setBulkData('');
      setDialogOpen(false);
      loadDashboardData();
    } catch (error) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to upload movies'
      });
    }
  };

  const handleDelete = async (id, type) => {
    try {
      if (type === 'movie') {
        await mongoAdminApi.deleteMovie(id);
      } else {
        await mongoAdminApi.deleteShow(id);
      }
      addNotification({
        type: 'success',
        message: `${type} deleted successfully`
      });
      loadDashboardData();
    } catch (error) {
      addNotification({
        type: 'error',
        message: `Failed to delete ${type}`
      });
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value || 0}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ bgcolor: `${color}.main`, borderRadius: '50%', p: 2 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (!isAdmin(user)) {
    return (
      <Alert severity="error">
        Access denied. Admin privileges required.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Content Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setDialogType('create');
              setDialogOpen(true);
            }}
          >
            Add Movie
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => {
              setDialogType('bulk');
              setDialogOpen(true);
            }}
          >
            Bulk Upload
          </Button>
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={() => {
              setDialogType('tmdb-update');
              setDialogOpen(true);
            }}
          >
            TMDB Update
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<Analytics />} label="Overview" />
        <Tab icon={<Movie />} label="Manage Movies" />
        <Tab icon={<TrendingUp />} label="Analytics" />
        <Tab icon={<Warning />} label="Data Quality" />
      </Tabs>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Box>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Movies"
                value={stats.movies.total}
                icon={<Movie sx={{ color: 'white' }} />}
                color="primary"
                subtitle={`${stats.movies.recent || 0} added this month`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Shows"
                value={stats.shows.total}
                icon={<Tv sx={{ color: 'white' }} />}
                color="secondary"
                subtitle={`${stats.shows.recent || 0} added this month`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Trending Content"
                value={trending.length}
                icon={<TrendingUp sx={{ color: 'white' }} />}
                color="success"
                subtitle="Hot right now"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Duplicates Found"
                value={duplicates.length}
                icon={<Warning sx={{ color: 'white' }} />}
                color="warning"
                subtitle="Need attention"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={() => {
                          setDialogType('bulk');
                          setDialogOpen(true);
                        }}
                      >
                        Bulk Upload
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<FindInPage />}
                        onClick={loadDuplicates}
                      >
                        Find Duplicates
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Schedule />}
                        onClick={loadDashboardData}
                      >
                        Refresh Data
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Analytics />}
                        onClick={loadDashboardData}
                      >
                        View Analytics
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Content Health
                  </Typography>
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">Movies Database</Typography>
                      <Chip label="Healthy" color="success" size="small" />
                    </Box>
                    <LinearProgress variant="determinate" value={95} color="success" />
                  </Box>
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">Shows Database</Typography>
                      <Chip label="Healthy" color="success" size="small" />
                    </Box>
                    <LinearProgress variant="determinate" value={92} color="success" />
                  </Box>
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">Data Quality</Typography>
                      <Chip label={duplicates.length > 0 ? "Issues" : "Good"} color={duplicates.length > 0 ? "warning" : "success"} size="small" />
                    </Box>
                    <LinearProgress variant="determinate" value={duplicates.length > 0 ? 75 : 98} color={duplicates.length > 0 ? "warning" : "success"} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Manage Movies Tab */}
      {activeTab === 1 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight="bold">
                      Movie Management
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Refresh />}
                      onClick={() => loadMovies(1)}
                    >
                      Refresh
                    </Button>
                  </Box>
                  
                  {/* Advanced Search Filters */}
                  <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                    <Typography variant="h6" gutterBottom startIcon={<FilterList />}>
                      Advanced Search & Filters
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          placeholder="Search by title, cast, director..."
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth>
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={category}
                            label="Category"
                            onChange={(e) => {
                              setCategory(e.target.value);
                              handleFilterChange();
                            }}
                          >
                            <MenuItem value="all">All Movies</MenuItem>
                            <MenuItem value="trending">Trending</MenuItem>
                            <MenuItem value="popular">Popular</MenuItem>
                            <MenuItem value="latest">Latest</MenuItem>
                            <MenuItem value="top_rated">Top Rated</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth>
                          <InputLabel>Language</InputLabel>
                          <Select
                            value={languageFilter}
                            label="Language"
                            onChange={(e) => {
                              setLanguageFilter(e.target.value);
                              handleFilterChange();
                            }}
                          >
                            <MenuItem value="">All Languages</MenuItem>
                            <MenuItem value="Hindi">Hindi</MenuItem>
                            <MenuItem value="English">English</MenuItem>
                            <MenuItem value="Telugu">Telugu</MenuItem>
                            <MenuItem value="Tamil">Tamil</MenuItem>
                            <MenuItem value="Malayalam">Malayalam</MenuItem>
                            <MenuItem value="Kannada">Kannada</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth>
                          <InputLabel>Genre</InputLabel>
                          <Select
                            value={genreFilter}
                            label="Genre"
                            onChange={(e) => {
                              setGenreFilter(e.target.value);
                              handleFilterChange();
                            }}
                          >
                            <MenuItem value="">All Genres</MenuItem>
                            <MenuItem value="Action">Action</MenuItem>
                            <MenuItem value="Drama">Drama</MenuItem>
                            <MenuItem value="Comedy">Comedy</MenuItem>
                            <MenuItem value="Romance">Romance</MenuItem>
                            <MenuItem value="Thriller">Thriller</MenuItem>
                            <MenuItem value="Horror">Horror</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth>
                          <InputLabel>Sort By</InputLabel>
                          <Select
                            value={sortBy}
                            label="Sort By"
                            onChange={(e) => {
                              setSortBy(e.target.value);
                              handleFilterChange();
                            }}
                          >
                            <MenuItem value="name">Title</MenuItem>
                            <MenuItem value="rating">Rating</MenuItem>
                            <MenuItem value="release_date">Release Date</MenuItem>
                            <MenuItem value="popularity">Popularity</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                  
                  {/* Results Summary */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      {filteredMovies.length > 0 ? `Showing ${filteredMovies.length} movies` : 'No movies found'}
                      {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
                    </Typography>
                  </Box>
                  
                  {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                      <Typography sx={{ ml: 2 }}>Loading movies...</Typography>
                    </Box>
                  ) : filteredMovies.length > 0 ? (
                    <>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Poster</TableCell>
                              <TableCell>Title</TableCell>
                              <TableCell>Language</TableCell>
                              <TableCell>Genre</TableCell>
                              <TableCell>Rating</TableCell>
                              <TableCell>Release Date</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredMovies.map((movie) => (
                              <TableRow key={movie._id || movie.id}>
                                <TableCell>
                                  <Box
                                    component="img"
                                    src={movie.poster || '/placeholder-movie.svg'}
                                    alt={movie.name}
                                    onError={(e) => {
                                      e.target.src = '/placeholder-movie.svg';
                                    }}
                                    sx={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 1 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {movie.name}
                                  </Typography>
                                  {movie.hero && (
                                    <Typography variant="caption" color="text.secondary">
                                      {movie.hero}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>{movie.language || 'N/A'}</TableCell>
                                <TableCell>{movie.genre || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={movie.rating ? `${movie.rating}/10` : 'N/A'} 
                                    size="small" 
                                    color={movie.rating >= 7 ? 'success' : movie.rating >= 5 ? 'warning' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {movie.release_date ? new Date(movie.release_date).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditMovie(movie)}
                                    color="primary"
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => {
                                      const movieId = movie._id || movie.id;
                                      if (movieId) {
                                        handleDelete(movieId, 'movie');
                                      } else {
                                        addNotification({ type: 'error', message: 'Movie ID is missing' });
                                      }
                                    }}
                                    color="error"
                                  >
                                    <Delete />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={3}>
                          <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(event, page) => {
                              setCurrentPage(page);
                              loadMovies(page);
                            }}
                            color="primary"
                            size="large"
                          />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Alert severity="info">
                      No movies found. Try adjusting your search filters or click "Refresh" to load data.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Analytics Tab */}
      {activeTab === 2 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Trending Movies
                  </Typography>
                  {trending.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Rating</TableCell>
                            <TableCell>Release Date</TableCell>
                            <TableCell>Genre</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {trending.slice(0, 10).map((movie) => (
                            <TableRow key={movie._id}>
                              <TableCell>{movie.name}</TableCell>
                              <TableCell>
                                <Chip label={movie.rating || 'N/A'} size="small" />
                              </TableCell>
                              <TableCell>{movie.release_date || 'N/A'}</TableCell>
                              <TableCell>{movie.genre?.join(', ') || 'N/A'}</TableCell>
                              <TableCell>
                                <IconButton size="small" onClick={() => {
                                  setMovieForm(movie);
                                  setDialogType('edit');
                                  setDialogOpen(true);
                                }}>
                                  <Edit />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDelete(movie._id, 'movie')}>
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No trending data available</Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Data Quality Tab */}
      {activeTab === 3 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Duplicate Movies
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={loadDuplicates}
                    >
                      Scan for Duplicates
                    </Button>
                  </Box>
                  
                  {duplicates.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Similarity</TableCell>
                            <TableCell>Release Date</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {duplicates.map((duplicate, index) => (
                            <TableRow key={index}>
                              <TableCell>{duplicate.title}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${duplicate.similarity}%`} 
                                  color={duplicate.similarity > 90 ? 'error' : 'warning'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{duplicate.release_date}</TableCell>
                              <TableCell>
                                <Button size="small" color="error">
                                  Remove Duplicate
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="success">No duplicates found</Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Single Movie Dialog - Create/Edit */}
      <Dialog open={dialogOpen && (dialogType === 'create' || dialogType === 'edit')} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogType === 'edit' ? 'Edit Movie' : 'Add New Movie'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Movie Title"
                value={movieForm.name}
                onChange={(e) => setMovieForm({...movieForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Release Date"
                type="date"
                value={movieForm.release_date}
                onChange={(e) => setMovieForm({...movieForm, release_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rating"
                type="number"
                value={movieForm.rating}
                onChange={(e) => setMovieForm({...movieForm, rating: e.target.value})}
                inputProps={{ min: 0, max: 10, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Language"
                value={movieForm.language}
                onChange={(e) => setMovieForm({...movieForm, language: e.target.value})}
                placeholder="English, Hindi, Telugu"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Genre"
                value={movieForm.genre}
                onChange={(e) => setMovieForm({...movieForm, genre: e.target.value})}
                placeholder="Action, Drama, Comedy"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="TMDB ID"
                type="number"
                value={movieForm.tmdb_id || ''}
                onChange={(e) => setMovieForm({...movieForm, tmdb_id: e.target.value})}
                placeholder="123456"
                helperText="Optional: TMDB movie ID (numbers only)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hero"
                value={movieForm.hero || ''}
                onChange={(e) => setMovieForm({...movieForm, hero: e.target.value})}
                placeholder="Lead Actor"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heroine"
                value={movieForm.heroine || ''}
                onChange={(e) => setMovieForm({...movieForm, heroine: e.target.value})}
                placeholder="Lead Actress"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Director"
                value={movieForm.director || ''}
                onChange={(e) => setMovieForm({...movieForm, director: e.target.value})}
                placeholder="Director Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Music Director"
                value={movieForm.music || ''}
                onChange={(e) => setMovieForm({...movieForm, music: e.target.value})}
                placeholder="Music Composer"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Screenplay"
                value={movieForm.screenplay || ''}
                onChange={(e) => setMovieForm({...movieForm, screenplay: e.target.value})}
                placeholder="Screenplay Writer"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Editor"
                value={movieForm.editor || ''}
                onChange={(e) => setMovieForm({...movieForm, editor: e.target.value})}
                placeholder="Film Editor"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cameraman"
                value={movieForm.cameraman || ''}
                onChange={(e) => setMovieForm({...movieForm, cameraman: e.target.value})}
                placeholder="Cinematographer"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Production House"
                value={movieForm.production_house || ''}
                onChange={(e) => setMovieForm({...movieForm, production_house: e.target.value})}
                placeholder="Production Company"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="OTT Platforms"
                value={movieForm.ott || ''}
                onChange={(e) => setMovieForm({...movieForm, ott: e.target.value})}
                placeholder="Netflix, Amazon Prime, Disney+"
                helperText="Comma-separated list of streaming platforms"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="summary"
                value={movieForm.summary}
                onChange={(e) => setMovieForm({...movieForm, summary: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Poster Image
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField
                    fullWidth
                    label="Poster URL"
                    value={movieForm.poster}
                    onChange={(e) => setMovieForm({...movieForm, poster: e.target.value})}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const result = await s3ImageService.uploadPosterImage(file);
                            if (result.success && result.url) {
                              setMovieForm({...movieForm, poster: result.url});
                              addNotification({ type: 'success', message: 'Image uploaded successfully' });
                            }
                          } catch (error) {
                            addNotification({ type: 'error', message: 'Failed to upload image' });
                          }
                        }
                      }}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setDialogType('tmdb-search');
                      setDialogOpen(true);
                    }}
                  >
                    TMDB Search
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            try {
              // Validate required fields
              if (!movieForm.name || !movieForm.language || !movieForm.genre) {
                addNotification({ type: 'error', message: 'Name, Language, and Genre are required' });
                return;
              }
              
              // Clean data before sending
              const cleanMovieData = { ...movieForm };
              
              // Convert tmdb_id to integer or remove if empty
              if (cleanMovieData.tmdb_id && String(cleanMovieData.tmdb_id).trim() !== '') {
                cleanMovieData.tmdb_id = parseInt(cleanMovieData.tmdb_id, 10);
              } else {
                delete cleanMovieData.tmdb_id;
              }
              
              // Convert date to UTC format without timezone info
              if (cleanMovieData.release_date) {
                const date = new Date(cleanMovieData.release_date);
                cleanMovieData.release_date = date.toISOString().split('T')[0] + 'T00:00:00';
              }
              
              // Truncate long fields to prevent MongoDB validation errors
              if (cleanMovieData.poster && cleanMovieData.poster.length > 500) {
                cleanMovieData.poster = cleanMovieData.poster.substring(0, 500);
              }
              if (cleanMovieData.summary && cleanMovieData.summary.length > 200) {
                cleanMovieData.summary = cleanMovieData.summary.substring(0, 200);
              }
              
              if (dialogType === 'edit') {
                const movieId = movieForm._id || movieForm.id;
                console.log('Updating movie with ID:', movieId); // Debug log
                
                if (!movieId) {
                  addNotification({ type: 'error', message: 'Movie ID is missing' });
                  return;
                }
                
                await mongoAdminApi.updateMovie(movieId, cleanMovieData);
                addNotification({ type: 'success', message: 'Movie updated successfully' });
                loadMovies(currentPage); // Refresh the list
              } else {
                await mongoAdminApi.createMovie(cleanMovieData);
                addNotification({ type: 'success', message: 'Movie created successfully' });
                loadMovies(1); // Refresh the list
              }
              
              setMovieForm({ 
                name: '', genre: '', language: '', release_date: '', rating: '', 
                summary: '', poster: '', hero: '', heroine: '', director: '', 
                music: '', screenplay: '', editor: '', cameraman: '', 
                production_house: '', ott: '', tmdb_id: '' 
              });
              setDialogOpen(false);
              loadDashboardData();
            } catch (error) {
              console.error('Movie operation error:', error);
              addNotification({ type: 'error', message: error.message || `Failed to ${dialogType === 'edit' ? 'update' : 'create'} movie` });
            }
          }} variant="contained">{dialogType === 'edit' ? 'Update Movie' : 'Create Movie'}</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={dialogOpen && dialogType === 'bulk'} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Upload Movies</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={10}
            label="JSON Data"
            value={bulkData}
            onChange={(e) => setBulkData(e.target.value)}
            placeholder='[{"name": "Movie Title", "genre": ["Action"], "release_date": "2024-01-01", ...}]'
            sx={{ mt: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Upload up to 100 movies at once. Provide valid JSON array format.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkUpload} variant="contained">Upload</Button>
        </DialogActions>
      </Dialog>

      {/* TMDB Search Dialog */}
      <Dialog open={dialogOpen && dialogType === 'tmdb-search'} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Search TMDB Movies</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Search Movies"
              value={tmdbQuery}
              onChange={(e) => setTmdbQuery(e.target.value)}
              onKeyPress={async (e) => {
                if (e.key === 'Enter' && tmdbQuery.trim()) {
                  setLoading(true);
                  const results = await tmdbService.searchMovies(tmdbQuery);
                  setTmdbResults(results);
                  setLoading(false);
                }
              }}
              placeholder="Enter movie title..."
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={async () => {
                if (tmdbQuery.trim()) {
                  setLoading(true);
                  const results = await tmdbService.searchMovies(tmdbQuery);
                  setTmdbResults(results);
                  setLoading(false);
                }
              }}
              disabled={!tmdbQuery.trim() || loading}
            >
              Search
            </Button>
          </Box>
          
          {loading && <CircularProgress sx={{ mt: 2 }} />}
          
          {tmdbResults.length > 0 && (
            <Box sx={{ mt: 3, maxHeight: 400, overflow: 'auto' }}>
              <Grid container spacing={2}>
                {tmdbResults.map((movie) => (
                  <Grid item xs={12} key={movie.id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" gap={2}>
                          {movie.poster_path && (
                            <Box
                              component="img"
                              src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`}
                              alt={movie.title}
                              sx={{ width: 60, height: 90, objectFit: 'cover' }}
                            />
                          )}
                          <Box flex={1}>
                            <Typography variant="h6">{movie.title}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {movie.release_date} • Rating: {movie.vote_average}/10
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {movie.overview?.substring(0, 150)}...
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={async () => {
                                try {
                                  setLoading(true);
                                  
                                  // Get detailed movie data from TMDB
                                  const movieDetails = await tmdbService.getMovieDetails(movie.id);
                                  
                                  if (movieDetails) {
                                    // Check for existing movie with same TMDB ID
                                    const existingCheck = {
                                      name: movieDetails.name,
                                      tmdb_id: movieDetails.tmdb_id
                                    };
                                    
                                    // Transform TMDB data to match backend format
                                    const movieData = {
                                      name: movieDetails.name,
                                      genre: Array.isArray(movieDetails.genre) ? movieDetails.genre.join(', ') : movieDetails.genre,
                                      language: movieDetails.language,
                                      summary: movieDetails.summary ? movieDetails.summary.substring(0, 200) : '',
                                      rating: movieDetails.rating,
                                      release_date: movieDetails.release_date ? movieDetails.release_date + 'T00:00:00' : null,
                                      poster: movieDetails.poster ? movieDetails.poster.substring(0, 200) : '',
                                      tmdb_id: movieDetails.tmdb_id,
                                      hero: movieDetails.cast?.find(c => c.character)?.name || '',
                                      heroine: movieDetails.cast?.find(c => c.character && c.name !== movieDetails.cast[0]?.name)?.name || '',
                                      director: movieDetails.crew?.find(c => c.job === 'Director')?.name || ''
                                    };
                                    
                                    await mongoAdminApi.createMovie(movieData);
                                    
                                    addNotification({ 
                                      type: 'success', 
                                      message: `Movie "${movieDetails.name}" created successfully from TMDB` 
                                    });
                                    
                                    setDialogOpen(false);
                                    setTmdbQuery('');
                                    setTmdbResults([]);
                                    loadDashboardData();
                                  }
                                } catch (error) {
                                  console.error('TMDB movie creation error:', error);
                                  addNotification({ 
                                    type: 'error', 
                                    message: error.message || 'Failed to create movie from TMDB' 
                                  });
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              disabled={loading}
                            >
                              Add Movie
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setTmdbQuery('');
            setTmdbResults([]);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* TMDB Update Dialog */}
      <Dialog open={dialogOpen && dialogType === 'tmdb-update'} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update Movies from TMDB</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Search Movies to Update"
              value={tmdbQuery}
              onChange={(e) => setTmdbQuery(e.target.value)}
              onKeyPress={async (e) => {
                if (e.key === 'Enter' && tmdbQuery.trim()) {
                  setLoading(true);
                  const results = await tmdbService.searchMovies(tmdbQuery);
                  setTmdbResults(results);
                  setLoading(false);
                }
              }}
              placeholder="Enter movie title to find and update..."
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={async () => {
                if (tmdbQuery.trim()) {
                  setLoading(true);
                  const results = await tmdbService.searchMovies(tmdbQuery);
                  setTmdbResults(results);
                  setLoading(false);
                }
              }}
              disabled={!tmdbQuery.trim() || loading}
            >
              Search for Updates
            </Button>
          </Box>
          
          {loading && <CircularProgress sx={{ mt: 2 }} />}
          
          {tmdbResults.length > 0 && (
            <Box sx={{ mt: 3, maxHeight: 400, overflow: 'auto' }}>
              <Grid container spacing={2}>
                {tmdbResults.map((movie) => (
                  <Grid item xs={12} key={movie.id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" gap={2}>
                          {movie.poster_path && (
                            <Box
                              component="img"
                              src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`}
                              alt={movie.title}
                              sx={{ width: 60, height: 90, objectFit: 'cover' }}
                            />
                          )}
                          <Box flex={1}>
                            <Typography variant="h6">{movie.title}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {movie.release_date} • Rating: {movie.vote_average}/10 • TMDB ID: {movie.id}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {movie.overview?.substring(0, 150)}...
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={async () => {
                                try {
                                  setLoading(true);
                                  
                                  // Get detailed movie data from TMDB
                                  const movieDetails = await tmdbService.getMovieDetails(movie.id);
                                  
                                  if (movieDetails) {
                                    // Transform TMDB data to match backend format
                                    const updateData = {
                                      tmdb_id: movieDetails.tmdb_id,
                                      name: movieDetails.name,
                                      genre: Array.isArray(movieDetails.genre) ? movieDetails.genre.join(', ') : movieDetails.genre,
                                      language: movieDetails.language,
                                      story: movieDetails.description ? movieDetails.description.substring(0, 200) : '',
                                      rating: movieDetails.rating,
                                      release_date: movieDetails.release_date ? movieDetails.release_date + 'T00:00:00' : null,
                                      poster: movieDetails.poster ? movieDetails.poster.substring(0, 200) : '',
                                      hero: movieDetails.cast?.find(c => c.character)?.name || '',
                                      heroine: movieDetails.cast?.find(c => c.character && c.name !== movieDetails.cast[0]?.name)?.name || '',
                                      director: movieDetails.crew?.find(c => c.job === 'Director')?.name || ''
                                    };
                                    
                                    // Update existing movie by TMDB ID
                                    await mongoAdminApi.updateMovieByTmdbId(movieDetails.tmdb_id, updateData);
                                    
                                    addNotification({ 
                                      type: 'success', 
                                      message: `Movie "${movieDetails.name}" updated successfully from TMDB` 
                                    });
                                    
                                    setDialogOpen(false);
                                    setTmdbQuery('');
                                    setTmdbResults([]);
                                    loadDashboardData();
                                  }
                                } catch (error) {
                                  console.error('TMDB movie update error:', error);
                                  addNotification({ 
                                    type: 'error', 
                                    message: error.message || 'Failed to update movie from TMDB' 
                                  });
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              disabled={loading}
                            >
                              Update Movie
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setTmdbQuery('');
            setTmdbResults([]);
          }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentAdminPanel;